/**
 * `/circles/:id` — the ring in owner mode.
 *
 * The scene is the page; everything else hugs an edge or slides in from the
 * right. Data flows one way: an edit hits the API, the returned entry is
 * merged into the bundle, and the scene is re-rendered from it.
 */
import type {
  CalEvent,
  Calendar,
  CalendarBundle,
  EventPin,
  Source,
  Tag,
  User,
  View,
  ViewLayout,
} from '@circle/shared';
import { ApiError, api, pinApi } from '../api';
import { RemotePinStore } from '../../core/pins';
import { ownerTokens } from '../owner-tokens';
import type { PageContext, Teardown } from '../router';
import { Panel } from '../../ui/panel';
import { RingView, type Selection } from '../../ui/ring-view';
import { TopBar, footerBar, isNarrow, trackFooterHeight } from '../../ui/topbar';
import { button, coarsePointer, countBadge, el, setChildren, withBusy } from '../../ui/dom';
import { EventForm } from '../../ui/event-form';
import { openDetail } from '../../ui/detail-panel';
import { openCirclesPanel } from '../../ui/circles-panel';
import { CirclesDock } from '../../ui/circles-dock';
import { SearchField, eventFilter } from '../../ui/search';
import { PendingQueue } from '../../ui/pending';
import { openCollectDialog, openIcsDialog } from '../../ui/sources-panel';
import { ViewsBar } from '../../ui/views-bar';
import { WorkingLayout, completeLayout, defaultView, guardUnsaved } from '../../ui/circles';
import { openShareDialog } from '../../ui/share-dialog';
import { openSecurityPanel } from '../../ui/security-panel';
import { choiceDialog, openDialog } from '../../ui/dialog';
import { errorState, loadingState } from '../../ui/states';
import { errorMessage, toast, toastError } from '../../ui/toast';
import type { LayoutResetScope } from '../api';

export async function renderCalendarPage(ctx: PageContext): Promise<Teardown> {
  const calendarId = ctx.params.id!;
  ctx.host.appendChild(loadingState('Opening the ring'));

  let bundle: CalendarBundle;
  try {
    bundle = await api.getCalendar(calendarId);
  } catch (err) {
    ctx.host.replaceChildren(noAccessState(err, ctx));
    return;
  }

  // The bundle is authoritative for the token: a rotation elsewhere lands here.
  if (bundle.calendar.ownerToken) ownerTokens.set(calendarId, bundle.calendar.ownerToken);
  ownerTokens.setNote(calendarId, { name: bundle.calendar.name, claimed: bundle.calendar.claimed });

  let calendar: Calendar = bundle.calendar;
  let sources: Source[] = bundle.sources;
  let events: CalEvent[] = bundle.events;
  let tags: Tag[] = bundle.tags ?? [];
  let views: View[] = bundle.views ?? [];
  let eventPins: EventPin[] = bundle.eventPins ?? [];
  let user: User | null = null;

  ctx.host.replaceChildren();
  document.documentElement.dataset.surface = 'scene';

  const sceneHost = el('div', { class: 'scene-host' });
  ctx.host.appendChild(sceneHost);

  const today = new Date();

  // The moderation queue is the page's, not the panel's: the count has to be
  // on the Circles button and the dock before anyone opens either.
  const pending = new PendingQueue(calendarId);
  void pending.load();
  // Owner pins go to the server, so everyone sharing the ring sees the same
  // arrangement. A failed write rolls back and says so.
  const pins = new RemotePinStore(pinApi(calendarId), {
    onError: (error) => toastError(error),
    onRevert: () => ring.applyPins(),
  });

  // Which saved layout of the circles this page opens on: `?view=` wins, the
  // calendar's default otherwise. The hash still owns the camera, as it always
  // has, so a link with both in it says "this arrangement, seen from here".
  const asked = ctx.query.get('view');
  let currentViewId: string | null =
    views.find((v) => v.id === asked)?.id ?? defaultView(views)?.id ?? null;

  function layoutOf(viewId: string | null): ViewLayout {
    const view = views.find((v) => v.id === viewId);
    return completeLayout(tags, view?.layout);
  }

  const opening = views.find((v) => v.id === currentViewId);

  const ring = new RingView({
    host: sceneHost,
    bundle: current(),
    today,
    pins,
    layout: layoutOf(currentViewId),
    // The view's own camera, under the hash and over a fit of its layout.
    ...(opening?.camera ? { camera: opening.camera } : {}),
    // A fit aims at the part of the page the panel is not sitting on.
    insets: () => panel.insets(),
    onSelect: (selection) => onSelect(selection),
    // A card let go over a band joins that circle (docs/CIRCLES.md §9). The
    // ring only says so; the entry's tags are data and go through the API.
    onDropOnCircle: (eventId, tagId) => void joinCircle(eventId, tagId),
    onYearChange: (year) => {
      topbar.setYear(year);
      dock.setYear(year);
    },
  });

  /**
   * What is typed, as a question about one entry — asked once, of both. The
   * ring takes the entries that fail it out of the float layout and dims their
   * marks; the dock's list narrows with the same question, so the two never
   * disagree about what "Anna" means.
   */
  function applySearch(query: string): void {
    const keep = eventFilter(query, new Map(tags.map((t) => [t.id, t.name])));
    ring.setFilter(keep);
    dock.setSearch(keep);
  }

  const search = new SearchField({ onChange: applySearch });
  // Typing anywhere on the page goes to the field. The ring's own keys survive
  // it: they are named keys, and Space — the one printable one — is excluded.
  const detachSearch = search.attach();

  async function joinCircle(eventId: string, tagId: string): Promise<void> {
    const event = events.find((e) => e.id === eventId);
    const circle = tags.find((t) => t.id === tagId);
    if (!event || !circle) return;
    const had = event.tagIds ?? [];
    if (had.includes(tagId)) return;
    try {
      const updated = await api.setEventTags(calendarId, eventId, [...had, tagId]);
      upsert(updated);
      await reload();
      toast(`${event.title} is in ${circle.name} now.`);
    } catch (err) {
      toastError(err, 'That did not save.');
    }
  }

  /**
   * The arrangement being worked in. The scene hears about every change at
   * once and tweens the rings there; the *view* hears about it only when Save
   * is pressed (docs/CIRCLES.md §6). A view is a snapshot, not a recording.
   *
   * The camera rides along without being an edit: Save records where you are
   * looking from and Revert puts it back, but spinning or zooming never lights
   * the dot — looking is not arranging.
   */
  const working = new WorkingLayout({
    apply: (layout, animate) => ring.setViewLayout(layout, { animate }),
    camera: () => ring.camera(),
    applyCamera: (camera) => ring.setCamera(camera),
    save: (viewId, layout, camera) => api.patchView(calendarId, viewId, { layout, camera }),
    onError: (error) => toastError(error, 'That arrangement was not saved.'),
    onDirtyChange: () => viewsBar.paint(),
  });
  // `apply: false`: the RingView above opened on this layout already, and
  // handing it over again would refit and lose the camera it settled on.
  working.adopt(currentViewId, layoutOf(currentViewId), {
    apply: false,
    ...(opening?.camera ? { camera: opening.camera } : {}),
  });

  const panel = new Panel(ctx.host);

  function current(): CalendarBundle {
    return { calendar, sources, events, tags, views, eventPins };
  }

  function refreshScene(): void {
    ring.setBundle(current());
    paintEmpty();
  }

  /**
   * The empty ring says what to do with it. The product is a lens over
   * calendars you already have, and a new ring with nothing on it is the one
   * moment that has to be said; the card offers the three ways in and goes the
   * moment the first entry lands.
   */
  const emptyCard = el(
    'div',
    { class: 'empty-ring', role: 'region', 'aria-label': 'Nothing on the ring yet' },
    el('h2', { text: 'Nothing on the ring yet' }),
    el('p', {
      text: 'Your birthdays are probably written down somewhere already. Follow that calendar, ask people for theirs, or type one in.',
    }),
    el(
      'div',
      { class: 'empty-ring__actions' },
      button('Subscribe to a feed', () => openIcsDialog(sourceActions), { class: 'btn--small' }),
      button('Send a collect link', () => openCollectDialog(sourceActions), { class: 'btn--small' }),
      button('Add one by hand', () => openAddForm(), { class: 'btn--small btn--primary' }),
    ),
  );
  const sourceActions = {
    calendarId,
    sources: () => sources,
    reload,
    // A new source is worth seeing next to the others, so the panel opens on it.
    onDone: () => openCircles(),
  };

  function paintEmpty(): void {
    const show = events.length === 0;
    if (show && !emptyCard.isConnected) sceneHost.appendChild(emptyCard);
    if (!show && emptyCard.isConnected) emptyCard.remove();
  }

  async function reload(): Promise<void> {
    const next = await api.getCalendar(calendarId);
    calendar = next.calendar;
    sources = next.sources;
    events = next.events;
    tags = next.tags ?? [];
    views = next.views ?? [];
    eventPins = next.eventPins ?? [];
    // A circle made or deleted since the last load has to appear in both
    // layouts, or the scene would keep the tree it had — and a view that was
    // clean would suddenly read as edited because a name changed somewhere.
    working.rebase(layoutOf(currentViewId), completeLayout(tags, working.current));
    refreshScene();
    viewsBar.paint();
    dock.paint();
    // A circle renamed or an entry retagged changes what the query matches.
    applySearch(search.value);
    topbar.setName(calendar.name);
  }

  /** The Circles panel, on one circle when the dock asked for it. */
  function openCircles(expand?: string): void {
    openCirclesPanel({
      panel,
      calendarId,
      tags: () => tags,
      events: () => events,
      sources: () => sources,
      working,
      editable: true,
      reload,
      pending,
      ...(expand ? { expand } : {}),
    });
  }

  /**
   * Switch to a saved view: the rings tween, and the camera goes back with
   * them. Unsaved work on the view being left is asked about first — leaving it
   * is how an arrangement used to disappear without anyone deciding it should.
   */
  async function selectView(view: View): Promise<void> {
    if (view.id === currentViewId) return;
    if (!(await keepOrDrop())) return;
    currentViewId = view.id;
    working.adopt(view.id, completeLayout(tags, view.layout), {
      animate: true,
      ...(view.camera ? { camera: view.camera } : {}),
    });
    if (view.camera) ring.setCamera(view.camera);
    writeViewParam(view.id);
    viewsBar.paint();
  }

  /**
   * Ask what to do with unsaved work before it is thrown away. `true` means
   * carry on — either it was saved, or it was deliberately abandoned.
   */
  async function keepOrDrop(): Promise<boolean> {
    if (!working.dirty) return true;
    const name = views.find((v) => v.id === working.currentViewId)?.name ?? 'this view';
    const answer = await choiceDialog<'save' | 'discard' | 'cancel'>({
      title: 'Keep this arrangement?',
      description: `You have rearranged the circles since “${name}” was saved. Leaving it now loses the change.`,
      choices: [
        { value: 'discard', label: 'Discard', class: 'btn--quiet' },
        { value: 'save', label: `Save to “${name}”`, class: 'btn--primary' },
      ],
      cancel: 'cancel',
    });
    if (answer === 'cancel') return false;
    if (answer === 'discard') return true;
    return working.save();
  }

  /** `?view=<id>` in the address bar; the hash keeps the camera, as it always has. */
  function writeViewParam(viewId: string | null): void {
    const url = new URL(location.href);
    if (viewId) url.searchParams.set('view', viewId);
    else url.searchParams.delete('view');
    history.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  function sourceOf(event: CalEvent): Source | undefined {
    return sources.find((s) => s.id === event.sourceId);
  }

  function upsert(event: CalEvent): void {
    const index = events.findIndex((e) => e.id === event.id);
    if (index >= 0) events[index] = event;
    else events = [...events, event];
    refreshScene();
  }

  function onSelect(selection: Selection): void {
    if (!selection) {
      panel.close();
      return;
    }
    if (selection.kind === 'event') {
      const event = events.find((e) => e.id === selection.eventId);
      if (!event) return;
      openDetail({
        panel,
        calendarId,
        event,
        source: sourceOf(event),
        viewYear: ring.year,
        today,
        editable: true,
        tags: () => tags,
        onHighlightRing: (ringId) => ring.highlightRing(ringId),
        onTagsChanged: reload,
        onChanged: (updated) => upsert(updated),
        onDeleted: (id) => {
          events = events.filter((e) => e.id !== id);
          refreshScene();
        },
        onClose: () => ring.select(null),
      });
      // On a phone the sheet may have opened over the very mark that was
      // tapped. On a wide screen the panel is beside the ring and the camera
      // stays put — a second tap has to land where the first one did.
      if (isNarrow()) ring.revealSelection(panel.insets());
      return;
    }
    if (selection.kind === 'day') {
      openAddForm({ month: selection.month, day: selection.day });
      return;
    }
    // A band, or a circle's own ring line: the circle, opened where it is edited.
    if (selection.kind === 'circle') openCircles(selection.tagId);
  }

  function openAddForm(date?: { month: number; day: number }): void {
    const form = new EventForm({
      calendarId,
      viewYear: ring.year,
      tags: () => tags,
      onTagsChanged: reload,
      ...(date ? { date } : {}),
      onSaved: (event) => {
        form.destroy();
        upsert(event);
        panel.close();
        toast(`Added ${event.title}.`);
      },
      onCancel: () => {
        form.destroy();
        panel.close();
      },
    });
    panel.open({ title: date ? 'Add on this day' : 'Add to the ring', body: [form.root] }, { key: 'add' });
    form.focus();
  }

  // The count of collect submissions waiting rides on the button, so an owner
  // who never opens Circles still learns that somebody sent one.
  const circlesButton = button('Circles', () => openCircles(), { class: 'btn--small btn--badged' });
  const paintCirclesBadge = (): void => {
    setChildren(circlesButton, [
      'Circles',
      countBadge(pending.count, `${pending.count} waiting for you`),
    ]);
  };
  const unwatchPending = pending.watch(paintCirclesBadge);

  const topbar = new TopBar({
    name: calendar.name,
    year: ring.year,
    editableName: true,
    onRename: async (name) => {
      try {
        calendar = await api.patchCalendar(calendarId, { name });
      } catch (err) {
        toastError(err);
        topbar.setName(calendar.name);
      }
    },
    onYear: (year) => ring.setYear(year),
    search,
    // Add and Circles are what you came for and stay put; the other three fold
    // into the ⋯ menu on a phone rather than scrolling off the edge.
    actions: [
      button('Add', () => openAddForm(), { class: 'btn--small btn--primary' }),
      circlesButton,
    ],
    secondary: [
      {
        label: 'Share',
        onSelect: () =>
          openShareDialog({
            calendar,
            onUpdated: (updated) => {
              calendar = updated;
            },
            tags: () => tags,
            onTagsChanged: reload,
            sources: () => sources,
            onSourceUpdated: (updated) => {
              sources = sources.map((s) => (s.id === updated.id ? updated : s));
            },
          }),
      },
      {
        label: 'Keys',
        onSelect: () =>
          openSecurityPanel({
            panel,
            calendar,
            user,
            onUpdated: (updated) => {
              calendar = updated;
            },
            onSignedIn: (signedIn) => {
              user = signedIn;
            },
            // The page is showing a calendar that no longer exists; the panel
            // has already said so, so this only has to leave.
            onDeleted: () => void ctx.navigate('/'),
          }),
      },
      { label: 'Account', href: '/settings', class: 'btn--quiet' },
    ],
  });

  /**
   * Reset the cards. The dialog is a confirmation, because anyone the ring is
   * shared with sees the result too. `LayoutResetScope` also names the rings
   * (`ViewNode.override`), but nothing in the product can move or resize a
   * ring yet, so offering to put them back was a control over nothing; the
   * choice returns with ring dragging (docs/CIRCLES.md §7).
   */
  const resetLayout = button(
    'Reset layout',
    () => openResetDialog(),
    { class: 'btn--small btn--quiet' },
  );

  function openResetDialog(): void {
    const scope: LayoutResetScope = 'floats';
    const handle = openDialog({
      title: 'Reset the layout?',
      description:
        'Every card you dragged goes back to where the ring puts it. Anyone you shared this ring with sees the same arrangement, so they see this too.',
    });
    const cancel = button('Cancel', () => handle.close());
    const confirm = button(
      'Reset layout',
      () =>
        void withBusy(confirm, async () => {
          try {
            await api.resetLayout(calendarId, {
              scope,
              ...(currentViewId ? { viewId: currentViewId } : {}),
            });
            handle.close();
            // One request, made above. The store only has to forget what it
            // was holding, and the bundle is refetched for the rest.
            ring.resetPins({ serverReset: true });
            await reload();
            toast('Layout reset.');
          } catch (err) {
            toastError(err);
          }
        }),
      { class: 'btn--primary' },
    );
    handle.foot.append(el('span', { class: 'grow' }), cancel, confirm);
    handle.root.appendChild(handle.foot);
  }

  const viewsBar = new ViewsBar({
    calendarId,
    views: () => views,
    current: () => currentViewId,
    editable: true,
    onSelect: (view) => void selectView(view),
    // "Save as…" records the arrangement as it stands, not the one the view it
    // came from holds — making a new view out of what you have been fiddling
    // with is the whole reason to reach for it.
    capture: () => ({ layout: completeLayout(tags, working.current), camera: ring.camera() }),
    reload,
    dirty: () => working.dirty,
    onSave: async () => {
      if (await working.save()) toast('Arrangement saved.');
    },
    onRevert: () => working.revert({ animate: true }),
    onCurrentChange: (viewId) => {
      currentViewId = viewId;
      writeViewParam(viewId);
      const view = views.find((v) => v.id === viewId);
      // A view just made from the working layout, or the one left standing
      // after a delete: either way it is now the baseline, and nothing is dirty.
      if (view) {
        working.adopt(view.id, completeLayout(tags, view.layout), {
          ...(view.camera ? { camera: view.camera } : {}),
        });
      }
    },
  });

  const footer = footerBar(
    [
      viewsBar.root,
      // The same sentence as the front page's, and for the same reason: the
      // gestures named have to be the ones the reader's device has.
      el('div', {
        class: 'readout',
        text: coarsePointer()
          ? 'Drag to spin, pinch to zoom, two fingers to pan.'
          : 'Drag to spin, scroll to zoom, space and drag to pan.',
      }),
    ],
    [button('Reset view', () => ring.resetView(), { class: 'btn--small btn--quiet' }), resetLayout],
  );

  // The circles, under the name and always in reach: the same tree the panel
  // draws, reduced to what a glance and a toggle need (ui/circles-dock.ts).
  const dock = new CirclesDock({
    calendarId,
    tags: () => tags,
    events: () => events,
    sources: () => sources,
    working,
    editable: true,
    reload,
    pending,
    year: ring.year,
    today,
    onOpen: (circleId) => openCircles(circleId),
    // A row in the list is a card by another route: the same selection, the
    // same panel, and the camera moved the least it can to show the entry.
    onSelectEvent: (eventId) => {
      ring.select({ kind: 'event', eventId });
      ring.revealSelection(panel.insets());
    },
    onHighlight: (tagId) => ring.highlightRing(tagId),
  });

  ctx.host.append(topbar.root, dock.root, footer);
  paintEmpty();
  // The bottom sheet stops where the strip starts, and only this knows how tall
  // the strip is: it is one row or two depending on how many views there are.
  const untrackFooter = trackFooterHeight(footer);

  // Switching views asks first and Save and Revert are on the bar, but a reload
  // or a closed tab used to take the arrangement with it in silence.
  const unguard = guardUnsaved(working);

  // Knowing whether someone is signed in only changes the security panel's
  // wording, so it never blocks the ring.
  void api
    .me()
    .then((me) => {
      user = me?.user ?? null;
    })
    .catch(() => {});

  return () => {
    // Nothing to flush: an arrangement is saved when it is saved, and leaving
    // the page with the dot still lit means it was never meant to be kept.
    unguard();
    untrackFooter();
    detachSearch();
    search.destroy();
    unwatchPending();
    pending.destroy();
    dock.destroy();
    working.destroy();
    ring.destroy();
    panel.destroy();
    viewsBar.destroy();
    topbar.destroy();
    delete document.documentElement.dataset.surface;
  };
}

function noAccessState(err: unknown, ctx: PageContext): HTMLElement {
  const home = el('a', { class: 'btn', href: '/', text: 'Go to your calendars' });
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
    return errorState({
      title: 'You need the key for this ring',
      description:
        'This calendar opens with its owner link, or with the account it belongs to. Open the owner link you saved, or sign in.',
      actions: [home],
    });
  }
  if (err instanceof ApiError && err.status === 404) {
    return errorState({
      title: 'No ring here',
      description: 'This calendar does not exist, or it has been deleted.',
      actions: [home],
    });
  }
  return errorState({
    title: 'The ring did not load',
    description: errorMessage(err),
    actions: [button('Try again', () => void ctx.navigate(ctx.path, { replace: true })), home],
  });
}
